import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardV2 } from '../CardV2/CardV2';
import ListContainer from '../ListContainer/ListContainer';
import ListElement from '../ListElement/ListElement';
import ElementActions from '../ElementActions/ElementActions';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import TextArea from '../TextArea/TextArea';
import IconButton from '../IconButton/IconButton';
import { Icon } from '../Icon/Icon';
import deleteIcon from '../../assets/images/bin.svg';
import { isValidText } from '../../shared/validation';

type FormConditionItem = { id: string; text: string };

interface FormConditionsEditorProps {
  conditions: string[];
  onChange: (conditions: string[]) => void;
  idPrefix?: string;
}

export default function FormConditionsEditor({
  conditions,
  onChange,
  idPrefix = 'form-condition',
}: FormConditionsEditorProps) {
  const { t } = useTranslation();
  const [newItem, setNewItem] = useState('');

  const elements: FormConditionItem[] = conditions.map((text, i) => ({
    id: `${idPrefix}-${i}`,
    text,
  }));

  const handleMovedItem = (reordered: FormConditionItem[]) => {
    onChange(reordered.map((item) => item.text));
  };

  const handleDeleteItem = (index: number) => {
    onChange(conditions.filter((_, i) => i !== index));
  };

  const handleAddItem = () => {
    if (newItem.trim()) {
      onChange([...conditions, newItem.trim()]);
      setNewItem('');
    }
  };

  return (
    <CardV2>
      <div className="p-4">
        <div className="text-xs text-muted-foreground mb-4">
          {String(t('commission_contract_template.form_conditions_hint') ?? 'These conditions will be shown to brides when they fill out the request form to sell their dresses.')}
        </div>
        <ListContainer
          elements={elements}
          enableDragAndDrop
          onMovedItem={handleMovedItem}
          toElementFn={(item: FormConditionItem, index: number) => (
            <ListElement>
              <ElementActions
                actions={
                  <IconButton onClick={() => handleDeleteItem(index)}>
                    <Icon url={deleteIcon} glyph cssStyles="w-5" />
                  </IconButton>
                }
              >
                <div className="flex-1 min-w-0">{item.text}</div>
              </ElementActions>
            </ListElement>
          )}
        />
        <TextArea
          label={String(t('commission_contract_template.form_conditions.placeholder') ?? 'Add a condition for the request form...')}
          validationFn={(value) => !!value && isValidText(value, 5)}
          defaultValue={newItem}
          onChange={(e) => setNewItem(e.target.value)}
        />
        <div className="container flex flex-wrap space-x-5 mt-2">
          <PrimaryButton
            text={String(t('commission_contract_template.form_conditions.add_button') ?? 'Add condition')}
            fn={handleAddItem}
            isDisabled={!newItem.trim()}
          />
        </div>
      </div>
    </CardV2>
  );
}
